/* ==========================================================================
   Fine Art by Renee — motion layer
   GSAP 3.13 + ScrollTrigger, both served from this theme's own assets.

   The one rule this file obeys: motion is ADDED, never DEPENDED ON.
   Every element ships visible in the HTML and is only hidden by GSAP once
   GSAP has actually loaded and run. A blocked script, a slow phone or a
   reduced-motion preference leaves a perfectly readable page, never a blank
   one — which matters more here than any animation, because the content is
   the paintings.

   The vocabulary, so it stays consistent:
     rise      content lifts into place as it enters              (once)
     sweep     a grid reveals across, then down, 70ms apart       (once)
     settle    a card's label arrives a beat after its picture    (once)
     rule      a mauve hairline draws left-to-right under a heading
     lead      a short hairline draws downward between sections
     drift     already-cropped images move slower than the page   (scrubbed)
     progress  a 2px mauve rule across the top of the window
   ========================================================================== */

(function () {
  'use strict';

  if (!window.gsap || !window.ScrollTrigger) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  gsap.registerPlugin(ScrollTrigger);
  document.documentElement.classList.add('renee-motion');

  var EASE = 'power2.out';
  var DESKTOP = window.matchMedia('(min-width: 990px)');
  var TEMPLATE = (document.querySelector('main[data-template]') || {}).dataset;
  TEMPLATE = (TEMPLATE && TEMPLATE.template) || '';

  function all(sel, ctx) {
    return Array.prototype.slice.call((ctx || document).querySelectorAll(sel));
  }
  function one(sel, ctx) {
    return (ctx || document).querySelector(sel);
  }
  /* Claim an element for ONE effect. A heading takes both a rise and a rule,
     so the flag has to be per-effect rather than per-element. */
  function claim(el, tag) {
    if (!el) return false;
    var seen = (el.dataset.reneeMotion || '').split(' ');
    if (seen.indexOf(tag) > -1) return false;
    seen.push(tag);
    el.dataset.reneeMotion = seen.join(' ').trim();
    return true;
  }

  var refreshSoon = (function () {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(function () { ScrollTrigger.refresh(); }, 200);
    };
  })();

  /* ------------------------------------------------------------------ rise */
  /* immediateRender hides the element at this moment — after GSAP is proven
     to be here — rather than in CSS, which would risk hiding it forever. */
  function rise(el, opts) {
    opts = opts || {};
    if (!claim(el, 'rise')) return;
    var vars = {
      opacity: 0,
      y: opts.y == null ? 24 : opts.y,
      duration: opts.duration || 0.7,
      delay: opts.delay || 0,
      ease: EASE
    };
    if (!opts.now) {
      vars.scrollTrigger = { trigger: opts.trigger || el, start: 'top 88%', once: true };
    }
    gsap.from(el, vars);
  }

  function riseEach(selector, opts) {
    all(selector).forEach(function (el) { rise(el, opts); });
  }

  /* ----------------------------------------------------------------- sweep */
  /* A grid does not arrive all at once. It reads across the row and then
     down — the same path the eye takes anyway, just made explicit. */
  function sweep(items) {
    items = items.filter(function (i) { return claim(i, 'sweep'); });
    if (!items.length) return;

    ScrollTrigger.batch(items, {
      start: 'top 90%',
      once: true,
      onEnter: function (batch) {
        gsap.from(batch, {
          opacity: 0,
          y: 34,
          duration: 0.75,
          ease: EASE,
          stagger: { each: 0.07, from: 'start' },
          onStart: function () {
            batch.forEach(function (item, i) { settle(item, 0.22 + i * 0.07); });
          }
        });
      }
    });
  }

  /* ---------------------------------------------------------------- settle */
  /* The label lands a beat after the picture, so the eye reads the painting
     first and the title second. That order is the whole point. */
  function settle(item, delay) {
    var content = one('.card__content', item);
    if (!claim(content, 'settle')) return;
    gsap.from(content, { opacity: 0, y: 10, duration: 0.55, delay: delay || 0.22, ease: EASE });
  }

  /* ------------------------------------------------------------------ rule */
  /* A mauve hairline draws left-to-right under a section heading. It points
     the way into the row underneath it. */
  function rule(heading) {
    if (!claim(heading, 'rule')) return;
    var line = document.createElement('span');
    line.className = 'renee-rule';
    line.setAttribute('aria-hidden', 'true');
    heading.appendChild(line);

    gsap.fromTo(
      line,
      { scaleX: 0 },
      {
        scaleX: 1,
        duration: 0.9,
        ease: 'power3.out',
        scrollTrigger: { trigger: heading, start: 'top 85%', once: true }
      }
    );
  }

  /* ------------------------------------------------------------------ lead */
  /* A short vertical hairline that draws downward in the gap above a
     browsing section — the most literal "the next piece is this way" cue on
     the page. It sits inside existing padding, so it adds no height. */
  function lead(section) {
    if (!claim(section, 'lead')) return;
    var mark = document.createElement('div');
    mark.className = 'renee-lead';
    mark.setAttribute('aria-hidden', 'true');
    mark.innerHTML = '<span></span>';
    section.parentNode.insertBefore(mark, section);

    gsap.fromTo(
      mark.firstChild,
      { scaleY: 0 },
      {
        scaleY: 1,
        duration: 0.8,
        ease: 'power2.inOut',
        scrollTrigger: { trigger: mark, start: 'top 92%', once: true }
      }
    );
  }

  /* ----------------------------------------------------------------- drift */
  /* Applied ONLY to images that are already cropped to a square by the card
     — the subject tiles. A painting shown whole is never cropped to make it
     move; the work outranks the effect. */
  function drift(img) {
    if (!claim(img, 'drift')) return;
    gsap.set(img, { scale: 1.06 });
    gsap.fromTo(
      img,
      { yPercent: -2.5 },
      {
        yPercent: 2.5,
        ease: 'none',
        scrollTrigger: { trigger: img, start: 'top bottom', end: 'bottom top', scrub: true }
      }
    );
  }

  /* -------------------------------------------------------------- progress */
  function progress() {
    var bar = document.createElement('div');
    bar.className = 'renee-progress';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);

    gsap.fromTo(
      bar,
      { scaleX: 0 },
      {
        scaleX: 1,
        ease: 'none',
        scrollTrigger: { trigger: document.body, start: 'top top', end: 'bottom bottom', scrub: 0.3 }
      }
    );
  }

  /* ---------------------------------------------------------------- header */
  function header() {
    if (!one('.header-wrapper')) return;
    ScrollTrigger.create({
      start: 'top -60',
      end: 99999,
      toggleClass: { className: 'renee-scrolled', targets: '.header-wrapper' }
    });
  }

  /* ------------------------------------------------------------ hero intro */
  /* The old WordPress hero opened with a Slider Revolution sequence: the
     headline rose character by character from behind a mask with a 5 degree
     untwist, then the button dropped in from above. That sequence is dead on
     the live site (its module never initialises) but the settings are still in
     the page, and the movement is the site's signature. It is rebuilt here on
     the marquee head — over the artwork band, which keeps running underneath
     and keeps being whatever blocks Renee has chosen in the theme editor.
     Nothing about how she picks that art is touched. */

  /* Wrap every character in its own mask. Words stay unbreakable so the
     heading still wraps at spaces, and the whole split is hidden from screen
     readers behind the original text as an aria-label. */
  function splitChars(el) {
    if (!el || el.children.length) return null; // leave rich text alone
    var text = el.textContent;
    if (!text.trim() || text.length > 240) return null;

    var frag = document.createDocumentFragment();
    var chars = [];

    text.split(/(\s+)/).forEach(function (token) {
      if (!token) return;
      if (/^\s+$/.test(token)) {
        frag.appendChild(document.createTextNode(' '));
        return;
      }
      var word = document.createElement('span');
      word.className = 'renee-word';
      token.split('').forEach(function (ch) {
        var mask = document.createElement('span');
        mask.className = 'renee-char';
        var inner = document.createElement('span');
        inner.textContent = ch;
        mask.appendChild(inner);
        word.appendChild(mask);
        chars.push(inner);
      });
      frag.appendChild(word);
    });

    var holder = document.createElement('span');
    holder.setAttribute('aria-hidden', 'true');
    holder.appendChild(frag);

    el.setAttribute('aria-label', text.trim());
    el.textContent = '';
    el.appendChild(holder);
    return chars;
  }

  function heroIntro(ctx) {
    var hero = one('.renee-marquee', ctx);
    if (!claim(hero, 'intro')) return;

    var eyebrow = one('.renee-marquee__eyebrow', hero);
    var heading = one('.renee-marquee__heading', hero);
    var button = one('.renee-marquee__cta .button', hero);
    var chars = splitChars(heading);
    var tl = gsap.timeline();

    if (eyebrow) {
      tl.from(eyebrow, { opacity: 0, y: 14, duration: 0.7, ease: EASE }, 0.2);
    }

    if (chars) {
      /* The original: y 60%, 5 degrees, behind a mask, power3.inOut over 1.2s. */
      tl.from(chars, {
        yPercent: 60,
        rotate: 5,
        opacity: 0,
        duration: 1.1,
        ease: 'power3.inOut',
        stagger: 0.012
      }, 0.45);
    } else if (heading) {
      tl.from(heading, { opacity: 0, y: 24, duration: 0.9, ease: EASE }, 0.45);
    }

    if (button) {
      /* Drops in from above, the way the old "Explore" button did. The mask
         opens again afterwards so it never clips a focus ring. */
      var mask = document.createElement('span');
      mask.className = 'renee-mask';
      var slide = document.createElement('span');
      slide.className = 'renee-mask__inner';
      button.parentNode.insertBefore(mask, button);
      mask.appendChild(slide);
      slide.appendChild(button);

      /* The inner span is what moves, not the button: a theme is free to make
         a button display:inline, and transforms do nothing on an inline box. */
      tl.from(slide, { yPercent: -130, duration: 0.8, ease: 'power2.inOut' }, 1.55);
      tl.add(function () { mask.classList.add('renee-mask--open'); });
    }

    heroCue(hero, tl);
  }

  /* Below the call to action: a hairline and a chevron that arrive once the
     intro has settled, breathe gently, then get out of the way for good on the
     first scroll. */
  function heroCue(hero, tl) {
    var cta = one('.renee-marquee__cta', hero);
    if (!claim(cta, 'cue')) return;

    var cue = document.createElement('div');
    cue.className = 'renee-cue';
    cue.setAttribute('aria-hidden', 'true');
    cue.innerHTML =
      '<span class="renee-cue__line"></span>' +
      '<svg viewBox="0 0 16 10" fill="none" stroke="currentColor" stroke-width="1.4">' +
      '<path d="M1 1l7 7 7-7" stroke-linecap="square"/></svg>';
    cta.appendChild(cue);

    tl.fromTo(cue, { opacity: 0 }, { opacity: 1, duration: 0.8, ease: EASE }, 2.4);

    gsap.to(cue.querySelector('svg'), {
      y: 5,
      duration: 1.4,
      ease: 'sine.inOut',
      repeat: -1,
      yoyo: true
    });
    gsap.to(cue, {
      opacity: 0,
      duration: 0.4,
      scrollTrigger: { start: 'top -140', end: 99999, once: true }
    });
  }

  /* ============================================================== assembly */

  function galleries(ctx) {
    /* Section headings and their hairlines. */
    all('.collection__title .title, .collection-list-title, .title-wrapper h2.title, .title-wrapper-with-link .title, .renee-marquee__overlay', ctx)
      .forEach(function (h) { rise(h); rule(h); });

    /* The marquee's own eyebrow and heading belong to the hero intro, not to
       the generic rise — two tweens on the same opacity fight and one loses. */
    riseEach('.collection__description', { y: 18 });

    /* Product grids and subject tiles. */
    all('.product-grid, .collection-list', ctx).forEach(function (grid) {
      sweep(all('.grid__item', grid));
    });

    /* Drift, on the square-cropped subject tiles only. */
    all('.collection-list__item .card__media img', ctx).forEach(drift);
  }

  function homepage() {
    heroIntro();
    riseEach('.image-with-text__content > *, .rich-text__blocks > *', { y: 20 });
    riseEach('.blog .article-card-wrapper', { y: 26 });

    /* Drop a lead line into the gap above each browsing section. */
    all('#MainContent > .shopify-section').forEach(function (section) {
      if (one('.collection__title, .collection-list-title', section)) lead(section);
    });
  }

  function productPage() {
    /* No scroll trigger up here — the page opens with this sequence. */
    rise(one('.product__media-wrapper'), { y: 28, duration: 0.8, now: true });
    all('.product__info-container > *').forEach(function (el, i) {
      rise(el, { y: 16, duration: 0.6, delay: 0.12 + i * 0.06, now: true });
    });
    riseEach('.product__description, .product__accordion', { y: 18 });
  }

  function collectionPage() {
    riseEach('.collection-hero__title, .collection-hero__description', { y: 20, now: true });
  }

  function init() {
    galleries();
    if (TEMPLATE === 'index') homepage();
    if (TEMPLATE.indexOf('product') === 0) productPage();
    if (TEMPLATE.indexOf('collection') === 0) collectionPage();
    progress();
    header();
    ScrollTrigger.refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  /* Theme editor: re-run for a section the merchant just added or moved. */
  document.addEventListener('shopify:section:load', function (event) {
    galleries(event.target);
    if (TEMPLATE === 'index') heroIntro(event.target);
    ScrollTrigger.refresh();
  });

  /* Lazy images land long after the triggers were measured, and every one of
     them moves the start points below it. Re-measure as they arrive. */
  all('#MainContent img').forEach(function (img) {
    if (img.complete) return;
    img.addEventListener('load', refreshSoon, { once: true });
  });
  window.addEventListener('load', refreshSoon);
})();
